/**
 * Aritmetica e validazione del piano rate.
 *
 * Regola unica di tutto il file: gli importi si confrontano e si sommano in CENTESIMI
 * INTERI. In euro come float, tre rate da 1366,67 non fanno 4100,00 e il salvataggio
 * verrebbe rifiutato dal database per un errore che non esiste.
 */

import { IInstallmentDraft, IInstallment, IPaymentPlan } from '../models/payment-plan.model';

export const toCents = (amount: number): number => Math.round((Number(amount) || 0) * 100);

export const fromCents = (cents: number): number => Math.round(cents) / 100;

export const sumCents = (installments: Array<{ amount: number }>): number =>
    installments.reduce((acc, i) => acc + toCents(i.amount), 0);

/** Percentuale sul totale, arrotondata a 2 decimali. Solo per la UI: il DB ricalcola la sua. */
export const percentOf = (amount: number, total: number): number => {
    const totalCents = toCents(total);
    if (totalCents === 0) return 0;
    return Math.round((toCents(amount) / totalCents) * 10000) / 100;
};

/**
 * Importo in euro corrispondente a una percentuale del totale.
 *
 * Inverso di `percentOf`, usato dall'editor quando il TD scrive la rata in percentuale:
 * il valore battuto resta un'indicazione, quello che si salva e' sempre l'importo.
 * L'arrotondamento e' al centesimo, e passa dai centesimi interi come tutto il resto.
 */
export const amountFromPercent = (percent: number, total: number): number => {
    const totalCents = toCents(total);
    if (totalCents === 0) return 0;
    return fromCents(Math.round((totalCents * (Number(percent) || 0)) / 100));
};

/**
 * Dice se una rata e' gia' stata incassata, e quindi intoccabile.
 *
 * E' un parametro obbligatorio e non un campo della bozza: `toInstallmentDraft` non porta
 * `status`, quindi una versione che lo leggesse dalla riga non troverebbe mai nulla e
 * ridistribuirebbe in silenzio anche gli importi gia' pagati. Chi chiama ha sempre
 * `paidById` sottomano — l'unica fonte affidabile, perche' viene dal server.
 */
export type IsPaidInstallment = (installment: IInstallmentDraft) => boolean;

/**
 * Indice delle rate gia' incassate, per id. E' il posto in cui si guarda `status` per
 * decidere cosa e' bloccato: la bozza dell'editor quel campo non ce l'ha.
 */
export const paidInstallmentsById = (
    installments: IInstallment[] | undefined,
): Record<string, IInstallment> => {
    const map: Record<string, IInstallment> = {};
    installments?.forEach((inst) => {
        if (inst.status === 'paid') map[inst.id] = inst;
    });
    return map;
};

/** Predicato costruito sulla mappa delle rate incassate che la UI gia' tiene. */
export const isPaidIn =
    (paidById: Record<string, IInstallment>): IsPaidInstallment =>
    (installment) =>
        Boolean(installment.id && paidById[installment.id]);

/**
 * Ripartisce `totalCents` sulle rate NON pagate, in parti uguali.
 *
 * Le rate incassate restano al centesimo com'erano e il loro importo viene tolto dal
 * distribuibile: sono soldi gia' entrati, non c'e' modo di tornare indietro e il database
 * rifiuterebbe comunque la transazione (`guard_paid_installment_trigger`).
 *
 * Il resto della divisione finisce tutto sull'ultima rata modificabile: e' l'unico posto
 * dove puo' stare senza che la somma sbagli, e la piu' lontana nel tempo e' anche quella
 * che il cliente ha meno probabilita' di aver gia' visto.
 */
export const splitEvenly = <T extends IInstallmentDraft>(
    installments: T[],
    totalCents: number,
    isPaid: IsPaidInstallment,
): T[] => {
    const editableIdx = installments
        .map((inst, idx) => ({ inst, idx }))
        .filter(({ inst }) => !isPaid(inst))
        .map(({ idx }) => idx);

    if (editableIdx.length === 0) return installments;

    const lockedCents = installments
        .filter((inst) => isPaid(inst))
        .reduce((acc, inst) => acc + toCents(inst.amount), 0);

    const distributable = totalCents - lockedCents;
    if (distributable <= 0) return installments;

    const base = Math.floor(distributable / editableIdx.length);
    const next = [...installments];

    editableIdx.forEach((idx, position) => {
        const isLast = position === editableIdx.length - 1;
        const cents = isLast ? distributable - base * (editableIdx.length - 1) : base;
        next[idx] = { ...next[idx], amount: fromCents(cents) };
    });

    return next;
};

/**
 * Assorbe la differenza rispetto al totale sull'ultima rata modificabile, lasciando
 * intatte le altre. Serve quando il totale dell'itinerario cambia dopo il salvataggio.
 * Ritorna null se l'operazione produrrebbe una rata di importo nullo o negativo: in quel
 * caso il piano va rifatto a mano, non aggiustato.
 */
export const rebalanceToTotal = <T extends IInstallmentDraft>(
    installments: T[],
    totalCents: number,
    isPaid: IsPaidInstallment,
): T[] | null => {
    const lastEditable = [...installments].reverse().find((inst) => !isPaid(inst));
    if (!lastEditable) return null;

    const othersCents = installments
        .filter((inst) => inst !== lastEditable)
        .reduce((acc, inst) => acc + toCents(inst.amount), 0);

    const remainder = totalCents - othersCents;
    if (remainder <= 0) return null;

    return installments.map((inst) =>
        inst === lastEditable ? { ...inst, amount: fromCents(remainder) } : inst,
    );
};

export interface PlanValidation {
    /** Indice della rata -> insiemi di campi in errore, per evidenziare gli input. */
    fieldErrors: Record<number, Set<string>>;
    /** Chiavi i18n dei problemi che impediscono il salvataggio. */
    blocking: string[];
    /** Chiavi i18n dei problemi che vale la pena mostrare ma non bloccano. */
    warnings: string[];
    /** Differenza in centesimi fra totale itinerario e somma delle rate. */
    deltaCents: number;
}

/**
 * Le stesse regole girano anche in save_itinerary_payment_plan: qui servono per dare un
 * errore prima del round-trip, non come unica difesa.
 */
export const validatePlan = (
    installments: IInstallmentDraft[],
    options: { totalCents: number; paidById?: Record<string, IInstallment> },
): PlanValidation => {
    const { totalCents, paidById = {} } = options;
    const fieldErrors: Record<number, Set<string>> = {};
    const blocking: string[] = [];
    const warnings: string[] = [];

    const addError = (index: number, field: string) => {
        if (!fieldErrors[index]) fieldErrors[index] = new Set();
        fieldErrors[index].add(field);
    };

    if (installments.length === 0) {
        blocking.push('atLeastOne');
    }

    let previousDue: string | null = null;

    installments.forEach((inst, index) => {
        if (!inst.label?.trim()) {
            addError(index, 'label');
            blocking.push('labelRequired');
        }

        if (toCents(inst.amount) <= 0) {
            addError(index, 'amount');
            blocking.push('amountPositive');
        }

        if (!inst.due_date) {
            addError(index, 'due_date');
            blocking.push('dueDateRequired');
        } else {
            if (previousDue && inst.due_date <= previousDue) {
                addError(index, 'due_date');
                blocking.push('dueDatesNotIncreasing');
            }
            previousDue = inst.due_date;
        }

        // Una rata gia' incassata non puo' cambiare importo ne' scadenza: il database
        // rifiuterebbe comunque la transazione, ma qui l'errore arriva subito e indica
        // quale riga.
        const paid = inst.id ? paidById[inst.id] : undefined;
        if (paid) {
            if (toCents(paid.amount) !== toCents(inst.amount)) {
                addError(index, 'amount');
                blocking.push('paidImmutable');
            }
            if (paid.due_date !== inst.due_date) {
                addError(index, 'due_date');
                blocking.push('paidImmutable');
            }
        }

        if (inst.reminder_offsets.some((offset) => offset >= 0)) {
            warnings.push('reminderMustBeBeforeDue');
        }
    });

    const deltaCents = totalCents - sumCents(installments);
    if (deltaCents !== 0) {
        blocking.push('totalMismatch');
    }

    return {
        fieldErrors,
        blocking: [...new Set(blocking)],
        warnings: [...new Set(warnings)],
        deltaCents,
    };
};

/**
 * La rata che il cliente puo' pagare adesso: la prima ancora aperta.
 *
 * Le rate si saldano in ordine — pagare il saldo prima dell'acconto non ha senso — quindi
 * il pulsante di pagamento compare su una riga sola. Le rate annullate non contano.
 */
export const firstPayableInstallment = (
    plan: Pick<IPaymentPlan, 'installments'> | null | undefined,
): IInstallment | null =>
    plan?.installments.find((inst) => inst.status === 'pending') ?? null;

/**
 * Il piano prevede davvero piu' versamenti, e quindi va mostrato come elenco di rate.
 *
 * Sta qui perche' la stessa domanda se la ponevano tre punti diversi (proposta condivisa,
 * modale di pagamento del viaggiatore, riepilogo sotto il prezzo) e la rispondevano in tre
 * modi non equivalenti: due guardavano `installments.length > 1` contando anche le rate
 * annullate, uno pesava anche `mode`. Bastava annullare la seconda rata di un piano perche'
 * il cliente vedesse due CTA in concorrenza sullo stesso importo.
 *
 * Le rate annullate non contano: sono importi che il cliente non deve piu'.
 */
export const hasMultipleInstallments = (plan: IPaymentPlan | null | undefined): boolean =>
    Boolean(
        plan &&
            plan.mode === 'installments' &&
            plan.installments.filter((inst) => inst.status !== 'cancelled').length > 1,
    );

/**
 * Esiste un pagamento avviato dal cliente e non ancora concluso.
 *
 * Con i metodi ad addebito differito — l'addebito SEPA e soprattutto il bonifico bancario —
 * fra il momento in cui il cliente paga e quello in cui i fondi arrivano passano giorni. In
 * quella finestra la rata e' ancora `pending` ma NON va ne' ripagata ne' sollecitata: un
 * secondo pulsante attivo qui significa un secondo addebito vero.
 *
 * `payment_processing` e' derivato dal database (esiste una riga in `payments` con
 * status = 'pending' su quella rata): non e' ricalcolabile nel browser.
 */
export const hasPaymentInFlight = (plan: IPaymentPlan | null | undefined): boolean =>
    Boolean(plan?.installments.some((inst) => inst.payment_processing));

export interface PlanSummary {
    /** Rate non annullate: quelle di cui ha senso parlare al cliente. */
    totalCount: number;
    paidCount: number;
    paidCents: number;
    totalCents: number;
    remainingCents: number;
    /** Percentuale incassata, 0-100. */
    progress: number;
    /** true quando non resta nulla da pagare. */
    allPaid: boolean;
    /** Prossima rata da saldare, null se sono tutte pagate. */
    nextDue: IInstallment | null;
    /** true se la prossima rata e' gia' scaduta (flag calcolato dal database). */
    isOverdue: boolean;
}

/**
 * Riassunto del piano per la UI del cliente: quante rate, quante pagate, cosa resta e
 * quando scade la prossima. Sta qui e non nei componenti perche' lo usano sia il
 * riepilogo accanto al prezzo sia la modale di pagamento, e devono dire la stessa cosa.
 */
export const summarizePlan = (plan: IPaymentPlan | null | undefined): PlanSummary | null => {
    if (!plan) return null;

    const active = plan.installments.filter((inst) => inst.status !== 'cancelled');
    const paid = active.filter((inst) => inst.status === 'paid');

    const paidCents = sumCents(paid);
    // total_amount e' il totale su cui il piano e' stato costruito; se le rate sono state
    // annullate in parte, la somma delle attive e' la cifra che il cliente deve davvero.
    const totalCents = Math.max(sumCents(active), toCents(plan.total_amount));
    const nextDue = active.find((inst) => inst.status === 'pending') ?? null;

    return {
        totalCount: active.length,
        paidCount: paid.length,
        paidCents,
        totalCents,
        remainingCents: Math.max(totalCents - paidCents, 0),
        progress: totalCents > 0 ? Math.round((paidCents / totalCents) * 100) : 0,
        allPaid: nextDue === null,
        nextDue,
        isOverdue: Boolean(nextDue?.is_overdue),
    };
};

/** Aggiunge o toglie un offset mantenendo l'ordine cronologico. */
export const toggleReminderOffset = (offsets: number[], offset: number): number[] => {
    const exists = offsets.includes(offset);
    const next = exists ? offsets.filter((o) => o !== offset) : [...offsets, offset];
    return next.sort((a, b) => a - b);
};

/** ISO date (YYYY-MM-DD) spostata di `days`, senza dipendere dal fuso del browser. */
export const shiftIsoDate = (iso: string, days: number): string => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};

export const todayIso = (): string => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Scadenza proposta per una rata aggiunta in coda: 30 giorni dopo la piu' lontana gia'
 * fissata.
 *
 * Si guarda il massimo e non l'ultima riga della lista perche' basta che una scadenza sia
 * stata spostata all'indietro perche' l'ultima riga non sia piu' la piu' lontana: partendo
 * da quella, la data proposta cadrebbe fra due rate esistenti o addirittura sopra una di
 * loro, e la riga nascerebbe gia' in errore.
 */
export const nextInstallmentDueDate = (
    installments: Array<Pick<IInstallmentDraft, 'due_date'>>,
): string => {
    const dates = installments.map((inst) => inst.due_date).filter(Boolean);
    // Senza nessuna data fissata il riferimento e' oggi: una rata nuova con la casella
    // vuota costringerebbe comunque ad aprire il calendario.
    const latest = dates.length > 0 ? dates.reduce((a, b) => (b > a ? b : a)) : todayIso();
    // Mai prima di oggi. Quando il viaggio e' gia' saldato e il TD aggiunge un'attivita',
    // le rate in tabella sono tutte nel passato: partendo dall'ultima, la riga nuova
    // nascerebbe scaduta e il cron la solleciterebbe il giorno stesso.
    const reference = latest > todayIso() ? latest : todayIso();
    return shiftIsoDate(reference, 30);
};

/**
 * Finestra di date selezionabili per la rata in posizione `index`.
 *
 * Le scadenze devono crescere riga dopo riga (`due_dates_not_increasing` lato database),
 * quindi una rata non puo' finire sul giorno di un'altra ne' scavalcarla: il calendario
 * mostra disabilitato tutto cio' che sta fuori dall'intervallo fra la vicina precedente e
 * la successiva. Si cerca la prima riga *valorizzata* in ciascuna direzione — una rata
 * appena aggiunta puo' non avere ancora una data e non vincola nessuno.
 */
export const dueDateBounds = (
    installments: Array<Pick<IInstallmentDraft, 'due_date'>>,
    index: number,
): { min?: string; max?: string } => {
    let min: string | undefined;
    for (let i = index - 1; i >= 0; i--) {
        if (installments[i].due_date) {
            min = shiftIsoDate(installments[i].due_date, 1);
            break;
        }
    }

    let max: string | undefined;
    for (let i = index + 1; i < installments.length; i++) {
        if (installments[i].due_date) {
            max = shiftIsoDate(installments[i].due_date, -1);
            break;
        }
    }

    // Un piano gia' fuori ordine produrrebbe una finestra vuota, con il calendario tutto
    // disabilitato e nessun modo di uscirne. In quel caso resta solo il limite inferiore e
    // a segnalare il problema ci pensa la validazione.
    if (min && max && max < min) return { min };

    return { min, max };
};
